import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useI18n } from "../i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Languages } from "lucide-react";
import { LOGO_URL } from "../brand";

export default function Login() {
  const { login } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : t("loginError"));
    } finally { setLoading(false); }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between p-12 text-white lg:flex"
        style={{ backgroundImage: "linear-gradient(rgba(20,42,66,0.86), rgba(15,32,52,0.94)), url('https://images.unsplash.com/photo-1696068240127-eb0b884007f8?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200')", backgroundSize: "cover", backgroundPosition: "center" }}>
        <div className="inline-flex w-fit items-center rounded-xl bg-[#F7F2EA] px-4 py-3">
          <img src={LOGO_URL} alt="Urban Dreams Home" className="h-20 w-auto object-contain" />
        </div>
        <div>
          <h2 className="font-head text-4xl font-semibold leading-tight">Gestione operativa,<br />senza pensieri.</h2>
          <p className="mt-4 max-w-md text-slate-300">Appartamenti, prenotazioni, pulizie, manutenzioni, pagamenti e report economici — tutto in un unico posto.</p>
        </div>
        <div className="text-xs text-slate-400">© 2026 Urban Dreams Home S.R.L. · Milano</div>
      </div>

      <div className="flex w-full flex-col items-center justify-center bg-background px-6 lg:w-1/2">
        <div className="w-full max-w-sm fade-in">
          <div className="mb-8 flex items-center justify-between">
            <div className="flex items-center gap-3 lg:hidden">
              <div className="rounded-lg bg-[#F7F2EA] p-2"><img src={LOGO_URL} alt="Urban Dreams Home" className="h-12 w-auto object-contain" /></div>
            </div>
            <Button data-testid="login-lang-toggle" variant="outline" size="sm" className="ml-auto gap-2" onClick={() => setLang(lang === "it" ? "en" : "it")}>
              <Languages size={16} /> {lang.toUpperCase()}
            </Button>
          </div>
          <h1 className="font-head text-3xl font-semibold text-slate-900">{t("login")}</h1>
          <p className="mt-2 text-sm text-slate-500">{t("tagline")}</p>

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <Label htmlFor="email">{t("email")}</Label>
              <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5" required />
            </div>
            <div>
              <Label htmlFor="password">{t("password")}</Label>
              <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5" required />
              <Link to="/forgot-password" data-testid="forgot-password-link" className="mt-2 block text-right text-xs font-medium text-primary hover:underline">{t("forgotPassword")}</Link>
            </div>
            {error && <p data-testid="login-error" className="text-sm font-medium text-rose-600">{error}</p>}
            <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full bg-primary hover:bg-primary/90">
              {loading ? t("signingIn") : t("login")}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
